import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import DatePicker from '@components/common/DatePicker';
import featureNames from '@utils/featureToggle/constants';
import { withDefaultOptions } from '@components/common/DatePicker/HOC';
import { featureSelector } from '@store/common/selectors';
import { appliedSortSelector } from '@store/metricLens/base/selectors';
import getGranularity from '@utils/granularity';
import {
  setAppliedInterval,
  removeAppliedFilter,
  setAppliedGranularity,
  setAppliedShortcut,
  setLiveMode,
  setAppliedSort,
} from '@store/metricLens/base/actions';
import { track } from '@utils';
import {
  CLICK_ML_DATETIME_INTERVAL_CHANGE,
  CLICK_ML_LIVE,
  CLICK_ML_FILTER_CLOSE,
} from '@src/utils/sensor/track';
import {
  StyledScopeWrapper,
  WrapHeaderTitle,
  WrapHeaderTitleIcon,
  Delimiter,
  StyledCloseFilter,
  StyledDatePickerWrapper,
} from './styled';
import FilterLabel from '@components/common/FilterLabel';
import { useInvalidDimensionCheck } from '@src/utils/filter/useInvalidDimensionCheck';

const DatePickerWithOptions = withDefaultOptions(DatePicker);
// eslint-disable-next-line react/display-name
export default (props) => {
  const { filter, index, onFilterOpen, filterLength, liveMode } = props;
  const dispatch = useDispatch();
  const appliedSort = useSelector(appliedSortSelector);
  const filterNameTokens = filter.filter.name.split(/__/);
  const filterName = filterNameTokens.map((filterNameToken, index) => {
    if (['OR', 'AND'].includes(filterNameToken)) {
      return <Delimiter key={index}>{filterNameToken}</Delimiter>;
    }
    return filterNameToken;
  });
  const [currentFilter, setCurrentFilter] = useState(filter);

  useEffect(() => {
    setCurrentFilter(filter);
  }, [filter]);

  const duration = useSelector(featureSelector(featureNames.DATA_ACCESS_DURATION));

  const invalidDimensionWarning = useInvalidDimensionCheck()(filter.filter.rule);

  const onMLFilterTitleClickHandler = () => {
    onFilterOpen(index);
  };

  const wrapLine = filterNameTokens.join('').length < 20;

  const handleDateApply = (selectedDate, shortcut) => {
    if (shortcut !== 'live') {
      dispatch(setLiveMode(false));
      const newInterval = {
        from: selectedDate[0],
        to: selectedDate[1],
      };
      const granularity = getGranularity({
        from: newInterval.from,
        to: newInterval.to,
        minuteGranularity: false,
      });
      dispatch(setAppliedGranularity(granularity, index));
      dispatch(setAppliedInterval(newInterval, index));
      dispatch(setAppliedShortcut(shortcut, index));
      track({
        event: CLICK_ML_DATETIME_INTERVAL_CHANGE,
        data: {
          interval: newInterval,
          shortcut,
          granularity,
        },
      });
    } else {
      dispatch(setLiveMode(true));
      track({ event: CLICK_ML_LIVE });
    }
  };

  const handleRemoveFilter = () => {
    if (appliedSort.filter === index) {
      dispatch(
        setAppliedSort({
          filter: 0,
          metric: appliedSort.metric,
          order: appliedSort.order,
        }),
      );
    } else if (index < appliedSort.filter) {
      dispatch(
        setAppliedSort({
          filter: appliedSort.filter - 1,
          metric: appliedSort.metric,
          order: appliedSort.order,
        }),
      );
    }
    dispatch(removeAppliedFilter(index));
    track({ event: CLICK_ML_FILTER_CLOSE });
  };

  return (
    <StyledScopeWrapper>
      {wrapLine ? (
        <FilterLabel
          tagColor={filter.color}
          text={filterNameTokens.join(' ')}
          onClick={onMLFilterTitleClickHandler}
        />
      ) : (
        <Tooltip title={filterName}>
          <WrapHeaderTitle $borderColor={filter.color} onClick={onMLFilterTitleClickHandler}>
            {filterName}
          </WrapHeaderTitle>
        </Tooltip>
      )}

      <WrapHeaderTitleIcon>{invalidDimensionWarning}</WrapHeaderTitleIcon>

      <StyledDatePickerWrapper>
        <DatePickerWithOptions
          defaultValue={[moment(currentFilter.interval.from), moment(currentFilter.interval.to)]}
          defaultShortcut={liveMode ? 'live' : currentFilter.shortcut}
          onApply={handleDateApply}
          durationAllowed={duration}
          live
          placement={index === 2 ? 'right' : 'left'}
          granularity={60}
        />
      </StyledDatePickerWrapper>
      {filterLength > 1 ? (
        <StyledCloseFilter onClick={handleRemoveFilter}>
          <div>x</div>
        </StyledCloseFilter>
      ) : null}
    </StyledScopeWrapper>
  );
};
